import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CACHE_KEYS, isCacheValid } from '@/lib/cacheUtils';

export interface NavVisibility {
  build: boolean;
  gallery: boolean;
  myGear: boolean;
  docs: boolean;
}

export interface SiteSettings {
  id: string;
  site_name: string;
  logo_url: string | null;
  nav_visibility: NavVisibility;
  google_analytics_id: string | null;
  google_analytics_enabled: boolean;
  // SEO fields
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  seo_og_image: string | null;
  seo_twitter_handle: string;
  canonical_domain: string;
}

const CACHE_TTL = 1000 * 60 * 60; // 1 hour

interface CachedSiteSettings {
  data: SiteSettings;
  timestamp: number;
}

const DEFAULT_SETTINGS: SiteSettings = {
  id: '',
  site_name: 'CineRig Pro',
  logo_url: null,
  nav_visibility: {
    build: true,
    gallery: true,
    myGear: true,
    docs: true,
  },
  google_analytics_id: null,
  google_analytics_enabled: true,
  // SEO defaults
  seo_title: 'CineRig Pro - Professional Camera Rig Configuration',
  seo_description: 'Build your perfect camera rig with intelligent compatibility checking, power analysis, and weight optimization for cinema professionals.',
  seo_keywords: 'camera rig, cinema, RED, ARRI, Sony, Blackmagic, cage, accessories, filmmaking',
  seo_og_image: null,
  seo_twitter_handle: '@CineRigPro',
  canonical_domain: 'https://cinerig.pro',
};

const getCachedSettings = (): SiteSettings | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.SITE_SETTINGS);
    if (cached) {
      const parsed: CachedSiteSettings = JSON.parse(cached);
      if (isCacheValid(parsed.timestamp, CACHE_TTL)) {
        return parsed.data;
      }
    }
    return null;
  } catch {
    return null;
  }
};

const setCachedSettings = (settings: SiteSettings) => {
  try {
    const cached: CachedSiteSettings = { data: settings, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEYS.SITE_SETTINGS, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
};

export const clearSiteSettingsCache = () => {
  try {
    localStorage.removeItem(CACHE_KEYS.SITE_SETTINGS);
  } catch {
    // Ignore storage errors
  }
};

export function useSiteSettings() {
  const cached = getCachedSettings();
  const [settings, setSettings] = useState<SiteSettings>(cached || DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(!cached); // Only loading if no cache

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          // Authenticated users can access full settings including tracking IDs
          const { data, error } = await supabase
            .from('site_settings')
            .select('*')
            .limit(1)
            .maybeSingle();

          if (error) {
            console.error('Error fetching site settings:', error);
            return;
          }

          if (data) {
            const newSettings: SiteSettings = {
              id: data.id,
              site_name: data.site_name,
              logo_url: data.logo_url,
              nav_visibility: data.nav_visibility as unknown as NavVisibility,
              google_analytics_id: data.google_analytics_id || null,
              google_analytics_enabled: data.google_analytics_enabled ?? true,
              // SEO fields
              seo_title: data.seo_title || DEFAULT_SETTINGS.seo_title,
              seo_description: data.seo_description || DEFAULT_SETTINGS.seo_description,
              seo_keywords: data.seo_keywords || DEFAULT_SETTINGS.seo_keywords,
              seo_og_image: data.seo_og_image || null,
              seo_twitter_handle: data.seo_twitter_handle || DEFAULT_SETTINGS.seo_twitter_handle,
              canonical_domain: data.canonical_domain || DEFAULT_SETTINGS.canonical_domain,
            };
            setSettings(newSettings);
            setCachedSettings(newSettings);
          }
        } else {
          // Unauthenticated users get public settings only (no tracking IDs)
          const { data, error } = await supabase
            .from('public_site_settings')
            .select('*')
            .limit(1)
            .maybeSingle();

          if (error) {
            console.error('Error fetching public site settings:', error);
            return;
          }

          if (data) {
            const newSettings: SiteSettings = {
              id: data.id,
              site_name: data.site_name,
              logo_url: data.logo_url,
              nav_visibility: data.nav_visibility as unknown as NavVisibility,
              google_analytics_id: data.google_analytics_id || null,
              google_analytics_enabled: data.google_analytics_enabled ?? true,
              // SEO fields from public view
              seo_title: data.seo_title || DEFAULT_SETTINGS.seo_title,
              seo_description: data.seo_description || DEFAULT_SETTINGS.seo_description,
              seo_keywords: data.seo_keywords || DEFAULT_SETTINGS.seo_keywords,
              seo_og_image: data.seo_og_image || null,
              seo_twitter_handle: data.seo_twitter_handle || DEFAULT_SETTINGS.seo_twitter_handle,
              canonical_domain: data.canonical_domain || DEFAULT_SETTINGS.canonical_domain,
            };
            setSettings(newSettings);
            setCachedSettings(newSettings);
          }
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();

    // Subscribe to changes
    const channel = supabase
      .channel('site_settings_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'site_settings' },
        (payload) => {
          if (payload.new) {
            const data = payload.new as any;
            const newSettings: SiteSettings = {
              id: data.id,
              site_name: data.site_name,
              logo_url: data.logo_url,
              nav_visibility: data.nav_visibility as unknown as NavVisibility,
              google_analytics_id: data.google_analytics_id || null,
              google_analytics_enabled: data.google_analytics_enabled ?? true,
              // SEO fields
              seo_title: data.seo_title || DEFAULT_SETTINGS.seo_title,
              seo_description: data.seo_description || DEFAULT_SETTINGS.seo_description,
              seo_keywords: data.seo_keywords || DEFAULT_SETTINGS.seo_keywords,
              seo_og_image: data.seo_og_image || null,
              seo_twitter_handle: data.seo_twitter_handle || DEFAULT_SETTINGS.seo_twitter_handle,
              canonical_domain: data.canonical_domain || DEFAULT_SETTINGS.canonical_domain,
            };
            setSettings(newSettings);
            setCachedSettings(newSettings);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { settings, isLoading };
}
